/**
 * Organization usage & spend dashboard (Enterprise).
 *
 * Every figure is a floor: the store holds privacy buckets, never raw counts,
 * and a bucket's lower bound is the only number that can be summed honestly.
 * The dollar figure is therefore an *estimate by construction* and the page
 * says so next to the number, with the rate it was computed from editable —
 * a spend estimate whose assumptions are hidden is marketing, not analytics.
 */

import { useState } from 'react'
import { Link, Navigate } from 'react-router-dom'

import { ApiError } from '@/shared/api/client'
import { useOrgUsage, useOrganization, useUsageExport } from '@/shared/api/hooks'
import { AppShell, PageHead } from '@/shared/ui/Chrome'
import {
  Button,
  ErrorNotice,
  Field,
  Loading,
  Notice,
  Panel,
  Readout,
  formatRelative,
} from '@/shared/ui/primitives'
import { NoWorkspace, useCurrentOrgId } from './Team'

/** Blended $/Mtok defaults, editable. Families the backend can report. */
const DEFAULT_RATES: Record<string, number> = {
  'claude-opus': 30,
  'claude-sonnet': 9,
  'claude-haiku': 2.5,
  gpt: 10,
  gemini: 8,
  other: 10,
}

const modelLabels: Record<string, string> = {
  'claude-opus': 'Claude Opus',
  'claude-sonnet': 'Claude Sonnet',
  'claude-haiku': 'Claude Haiku',
  gpt: 'GPT',
  gemini: 'Gemini',
  other: 'Other models',
}

function compact(n: number): string {
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`
  return String(n)
}

export function TeamUsage() {
  const orgId = useCurrentOrgId()
  if (!orgId) return <NoWorkspace />
  return <UsageDashboard orgId={orgId} />
}

function UsageDashboard({ orgId }: { orgId: string }) {
  const { data: org } = useOrganization(orgId)
  const isEnterprise = org?.plan === 'enterprise'
  const usage = useOrgUsage(orgId, isEnterprise && org?.role !== 'member')
  const exportRoi = useUsageExport(orgId)
  const [rate, setRate] = useState(15)

  const forbidden = usage.error instanceof ApiError && usage.error.isForbidden

  if (org && org.role === 'member') {
    return <Navigate to="/team" replace />
  }

  return (
    <AppShell>
      <PageHead
        index="10"
        eyebrow="Workspace"
        title="Usage & savings"
        subtitle="What your organization's installs indexed, consumed and saved."
      />

      {org && !isEnterprise && (
        <Panel title="Included in Archis Enterprise" index="01" tone="signal">
          <div className="stack" style={{ ['--gap' as string]: 'var(--s4)' }}>
            <p className="dim" style={{ margin: 0, maxWidth: '60ch' }}>
              The organization-wide usage and spend dashboard — token volumes per model
              family, direct LLM savings from context reduction, and install reach — is exclusive to the{' '}
              <strong>Enterprise plan</strong>. Everything is measured in privacy buckets: no source code,
              no repository names, no per-person tracking.
            </p>
            <div className="row" style={{ gap: 'var(--s3)', flexWrap: 'wrap' }}>
              <Link to="/pricing#enterprise">
                <Button variant="primary">Contact Enterprise Sales</Button>
              </Link>
              <Link to="/pricing#matrix">
                <Button variant="ghost">Compare all tier capabilities →</Button>
              </Link>
            </div>
          </div>
        </Panel>
      )}

      {isEnterprise && (
        <>
          {usage.isPending && <Loading label="Aggregating usage" />}
          {!forbidden && <ErrorNotice error={usage.error} />}

          {usage.data && (
            <div className="stack" style={{ ['--gap' as string]: 'var(--s5)' }}>
              <div className="readout-grid">
                <div>
                  <Readout value={usage.data.installs} caption="active installs" />
                </div>
                <div>
                  <Readout value={usage.data.indexes} caption="index runs" />
                </div>
                <div>
                  <Readout
                    value={`≥${compact(usage.data.tokens_saved)}`}
                    caption="tokens saved"
                    tone="signal"
                  />
                </div>
                <div>
                  <Readout
                    value={usage.data.reduction || '—'}
                    caption="typical context reduction"
                  />
                </div>
              </div>

              <Panel title="Estimated savings" index="02">
                <div className="stack" style={{ ['--gap' as string]: 'var(--s4)' }}>
                  <div className="row" style={{ gap: 'var(--s5)', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <Readout
                      value={`≥$${compact(Math.floor((usage.data.tokens_saved / 1_000_000) * rate))}`}
                      caption="estimated LLM spend avoided"
                      tone="signal"
                    />
                    <Field label="Blended rate ($ / Mtok)" hint="Adjust to your actual contract pricing.">
                      <input
                        className="input"
                        type="number"
                        min={0}
                        step={0.5}
                        value={rate}
                        onChange={(event) => setRate(Number(event.target.value) || 0)}
                        style={{ maxWidth: 140 }}
                      />
                    </Field>
                  </div>
                  <p className="mono-note" style={{ margin: 0 }}>
                    A floor, not an average: token figures are recorded in coarse privacy
                    buckets and only each bucket's lower bound is summed. The real number is
                    higher.
                  </p>
                  <div className="row" style={{ gap: 'var(--s3)', flexWrap: 'wrap', alignItems: 'center' }}>
                    <Button
                      size="sm"
                      loading={exportRoi.isPending}
                      onClick={() => exportRoi.mutate(rate)}
                    >
                      Export ROI summary (CSV)
                    </Button>
                    <span className="dim" style={{ maxWidth: '52ch' }}>
                      One page for whoever signs: tokens avoided, the estimate at this rate and at
                      each provider's list price, and the model families behind it.
                    </span>
                  </div>
                  <ErrorNotice error={exportRoi.error} />
                </div>
              </Panel>

              <Panel title="Model usage" index="03" padded={usage.data.models?.length ? false : true}>
                {usage.data.models?.length ? (
                  <div className="table-wrap">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Model family</th>
                          <th>Input tokens</th>
                          <th>Output tokens</th>
                          <th>Agent turns</th>
                          <th>Est. spend</th>
                        </tr>
                      </thead>
                      <tbody>
                        {usage.data.models.map((model) => {
                          const modelRate = DEFAULT_RATES[model.name] ?? rate
                          const spend =
                            ((model.input_tokens + model.output_tokens) / 1_000_000) * modelRate
                          return (
                            <tr key={model.name}>
                              <td>{modelLabels[model.name] ?? model.name}</td>
                              <td className="mono-note">≥{compact(model.input_tokens)}</td>
                              <td className="mono-note">≥{compact(model.output_tokens)}</td>
                              <td className="mono-note">{model.turns ? `≥${compact(model.turns)}` : '—'}</td>
                              <td className="mono-note">≥${compact(Math.floor(spend))}</td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <Notice tone="warn">
                    No model usage reported yet. Installs start reporting per-family token
                    volumes once they run a CLI version that emits them and your telemetry
                    policy allows organization attribution.
                  </Notice>
                )}
              </Panel>

              <p className="mono-note">
                Window since {formatRelative(usage.data.since)} · figures honour your
                telemetry policy ({org?.policies.telemetry_mode}) — events are attributed to
                the workspace only when the policy allows it.
              </p>
            </div>
          )}
        </>
      )}
    </AppShell>
  )
}
